import { useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, FileDown, PenLine, UserPlus, Handshake, X, Save, ShieldCheck, Building2, User, Mail, Wallet, Send, RefreshCcw, ChevronDown, ChevronRight, Trophy, TrendingUp, AlertTriangle, CalendarClock, Banknote, Play, Unplug, Lock, LockOpen } from "lucide-react";
import { api } from "../../lib/api";

const inputCls = "w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] placeholder:text-[#1e3d3a]/30 focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20 transition-colors duration-300";
const labelCls = "text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50 mb-1 block";
const btnGold = "inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-xs font-semibold text-[#f4f1e8] hover:bg-[#12302c] transition-colors duration-300 disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-3 py-1.5 text-xs font-medium text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300";

const BLANK = {
  partner_type: "individual", partner_name: "", signatory_name: "", signatory_title: "",
  contact_email: "", contact_phone: "", partner_address: "",
  company_name: "Elevare Solutions LLC", company_address: "", governing_state: "",
  revenue_pct: 10, commission_duration: "12_months", flat_fee_per_client: 0,
  default_role: "referrer", sales_pct: 5, upsell_pct: 10,
  payout_terms: "Net 30 days after the Company receives cleared payment from the referred Client",
  exclusivity_mode: "exclusive", term_months: 12, liquidated_damages: "", effective_date: "", notes: "",
  business_scale: "medium", payout_basis: "contract_value", tier_cap_pct: 35, auto_requote: false,
  rate_mode: "volume",
  spend_tiers: [{ min_monthly: 0, pct: 10 }, { min_monthly: 1000, pct: 15 }, { min_monthly: 2500, pct: 20 }, { min_monthly: 5000, pct: 25 }],
  tiers: [{ min_clients: 1, pct: 10 }, { min_clients: 3, pct: 12.5 }, { min_clients: 6, pct: 15 }, { min_clients: 10, pct: 20 }],
};

const SCALE_DEFAULTS = {
  small: { label: "Small (< $5k)", max: 5000, pct: 10, lump: 500, tiers: [{ min_clients: 1, pct: 10 }, { min_clients: 3, pct: 12.5 }, { min_clients: 6, pct: 15 }, { min_clients: 10, pct: 20 }] },
  medium: { label: "Medium ($5k–$25k)", min: 5000, max: 25000, pct: 12.5, lump: 1500, tiers: [{ min_clients: 1, pct: 12.5 }, { min_clients: 3, pct: 15 }, { min_clients: 6, pct: 20 }, { min_clients: 10, pct: 25 }] },
  large: { label: "Large (> $25k)", min: 25000, pct: 15, lump: 5000, tiers: [{ min_clients: 1, pct: 15 }, { min_clients: 3, pct: 20 }, { min_clients: 6, pct: 25 }, { min_clients: 10, pct: 30 }] },
};
const TIER_CAP = 35;
const scaleFor = (amt) => (amt >= 25000 ? "large" : amt >= 5000 ? "medium" : "small");
const tierPctFor = (tiers, n, cap = TIER_CAP) => {
  const s = [...(tiers || [])].sort((a, b) => a.min_clients - b.min_clients);
  let pct = s.length ? s[0].pct : 0;
  for (const t of s) if (n >= t.min_clients) pct = t.pct;
  return Math.min(Number(pct) || 0, cap);
};
const spendPctFor = (bands, monthly, cap = TIER_CAP) => {
  const s = [...(bands || [])].sort((a, b) => a.min_monthly - b.min_monthly);
  let pct = s.length ? s[0].pct : 0;
  const m = Number(monthly) || 0;
  for (const b of s) if (m >= b.min_monthly) pct = b.pct;
  return Math.min(Number(pct) || 0, cap);
};
// Effective referral % honoring the partner's rate mode.
const effectiveRefPct = ({ tiers, spendTiers, rateMode, n, monthly, cap = TIER_CAP }) => {
  const vol = tierPctFor(tiers, n, cap);
  if ((rateMode || "volume") === "volume" || monthly == null || monthly === "") return vol;
  const size = spendPctFor(spendTiers, monthly, cap);
  return rateMode === "size" ? size : Math.max(vol, size);
};
const RATE_MODES = [
  { v: "volume", label: "By volume — % rises with # of clients" },
  { v: "size", label: "By client size — % set by monthly spend" },
  { v: "higher", label: "Higher of the two" },
];

const DURATION = { first_payment: "First payment only", "12_months": "First 12 months", lifetime: "Lifetime of client" };
const EXCL = { exclusive: "Exclusive (refer us only)", rofr: "Right of first refusal", non_circumvention: "Non-circumvention only" };
const STATUS_COLORS = {
  draft: "bg-[#6b6b6b]/10 text-[#6b6b6b]", sent: "bg-[#a3823f]/15 text-[#a3823f]",
  signed: "bg-[#4d5a24]/15 text-[#4d5a24]", active: "bg-[#1e7d5a]/15 text-[#1e7d5a]",
  terminated: "bg-[#b23a48]/15 text-[#b23a48]",
};
// Per-deal (client) lifecycle statuses
const REF_STATUSES = ["referred", "signed", "active", "paid", "unpaid", "dropped", "chargeback"];
const REF_STATUS_LABEL = {
  referred: "Referred", signed: "Signed", active: "Active (working)", paid: "Paid",
  unpaid: "Unpaid", dropped: "Dropped", chargeback: "Chargeback",
};
const REF_STATUS_COLORS = {
  referred: "bg-[#6b6b6b]/10 text-[#6b6b6b]", signed: "bg-[#4d5a24]/15 text-[#4d5a24]",
  active: "bg-[#1e7d5a]/15 text-[#1e7d5a]", paid: "bg-[#c9a961]/20 text-[#8a6d2f]",
  unpaid: "bg-[#c98a3f]/15 text-[#a3823f]", dropped: "bg-[#6b6b6b]/15 text-[#6b6b6b]",
  chargeback: "bg-[#b23a48]/15 text-[#b23a48]",
};
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const money2 = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ROLES = [
  { v: "referrer", label: "Referrer", hint: "Brings the client" },
  { v: "sales", label: "Sales Partner", hint: "Brings + closes the deal" },
  { v: "embedded", label: "Embedded Partner", hint: "Brings + closes + grows the account" },
];
const ROLE_LABEL = { referrer: "Referrer", sales: "Sales Partner", embedded: "Embedded" };
const DURATION_MONTHS = { first_payment: 1, "12_months": 12, lifetime: 12 };

// Monthly commission for a recurring deal (mirrors backend _deal_monthly_commission).
function monthlyCommission({ monthly, role, refPct, salesPct, upsellPct, baseline }) {
  const m = Number(monthly) || 0;
  if (m <= 0) return 0;
  let total = m * (Number(refPct) || 0) / 100;
  if (role === "sales" || role === "embedded") total += m * (Number(salesPct) || 0) / 100;
  if (role === "embedded") total += Math.max(m - (Number(baseline) || 0), 0) * (Number(upsellPct) || 0) / 100;
  return total;
}

// Computed payout for a single stored deal (recurring projection, lump, or legacy %).
function dealPayout(r, p) {
  if (r.payout_type === "lump") return Number(r.lump_sum) || 0;
  const cap = Number(p.tier_cap_pct) || 35;
  const pct = Math.min(r.commission_pct != null ? Number(r.commission_pct) : Number(p.revenue_pct) || 0, cap);
  if (r.monthly_payment != null) {
    const dur = DURATION_MONTHS[p.commission_duration] || 12;
    const mc = monthlyCommission({
      monthly: r.monthly_payment, role: r.role || p.default_role || "referrer", refPct: pct,
      salesPct: r.sales_pct != null ? r.sales_pct : p.sales_pct, upsellPct: r.upsell_pct != null ? r.upsell_pct : p.upsell_pct,
      baseline: r.baseline_monthly });
    return mc * dur;
  }
  const flat = r.flat_fee != null ? Number(r.flat_fee) : Number(p.flat_fee_per_client) || 0;
  return (Number(r.revenue_to_date) || 0) * pct / 100 + flat;
}

async function downloadPdf(url, filename) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = blobUrl; a.download = filename; a.rel = "noopener";
    document.body.appendChild(a); a.click();
    setTimeout(() => { a.remove(); window.URL.revokeObjectURL(blobUrl); }, 2000);
  } catch {
    toast.error("Could not generate the PDF.");
  }
}

export default function ReferralPartners() {
  const [partners, setPartners] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [signFor, setSignFor] = useState(null);
  const [refFor, setRefFor] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [showMilestones, setShowMilestones] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [retention, setRetention] = useState({ alerts: [], counts: {}, total: 0 });
  const [showRetention, setShowRetention] = useState(false);
  const [showBoard, setShowBoard] = useState(false);
  const [boardSort, setBoardSort] = useState("mrr");
  const [schedule, setSchedule] = useState({ payout_day: 1, auto_send: false });
  const [batch, setBatch] = useState(null);
  const [showPayouts, setShowPayouts] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);

  const loadExtras = async () => {
    try { const { data } = await api.get("/admin/referral-retention"); setRetention(data); } catch { /* non-critical */ }
    try { const { data } = await api.get("/admin/referral-payout-schedule"); setSchedule(data); } catch { /* non-critical */ }
  };
  const saveSchedule = async (patch) => {
    setSchedule((s) => ({ ...s, ...patch }));
    try { await api.put("/admin/referral-payout-schedule", patch); toast.success("Payout schedule updated."); }
    catch { toast.error("Could not update schedule."); }
  };
  const prepareBatch = async () => {
    setBatchBusy(true);
    try { const { data } = await api.post("/admin/referral-payout-batch/prepare"); setBatch(data); toast.success(`Batch prepared — ${data.ready_count} Stripe-ready of ${data.count}.`); }
    catch { toast.error("Could not prepare the batch."); }
    finally { setBatchBusy(false); }
  };
  const runBatch = async () => {
    if (!batch) return;
    if (!window.confirm(`Send Stripe payouts to ${batch.ready_count} partner(s) totaling $${(batch.ready_total || 0).toLocaleString()}? This moves real money.`)) return;
    setBatchBusy(true);
    try { const { data } = await api.post(`/admin/referral-payout-batch/${batch.id}/run`); setBatch(data); toast.success(`Paid out $${(data.paid_total || 0).toLocaleString()}.`); await load(); }
    catch { toast.error("Could not run the batch."); }
    finally { setBatchBusy(false); }
  };

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const updateDealStatus = async (pid, rid, status) => {
    try {
      await api.patch(`/admin/referral-partners/${pid}/referrals/${rid}`, { status });
      toast.success(`Deal marked ${REF_STATUS_LABEL[status] || status}.`);
      await load();
    } catch { toast.error("Could not update the deal status."); }
  };

  const deleteDeal = async (pid, rid) => {
    if (!window.confirm("Remove this deal from the partner's ledger?")) return;
    try { await api.delete(`/admin/referral-partners/${pid}/referrals/${rid}`); toast.success("Deal removed."); await load(); }
    catch { toast.error("Could not remove the deal."); }
  };

  const load = async () => {
    try { const { data } = await api.get("/admin/referral-partners"); setPartners(data.partners || []); }
    catch { setPartners([]); toast.error("Could not load referral partners."); }
    try { const { data } = await api.get("/admin/referral-milestones"); setMilestones(data.milestones || []); }
    catch { /* non-critical */ }
    loadExtras();
  };
  useEffect(() => { load(); loadExtras(); }, []);

  const sendPayoutDigest = async () => {
    try {
      const { data } = await api.post("/admin/referral-payout-digest");
      if (data.sent) toast.success(`Digest emailed: $${Number(data.total_owed).toLocaleString()} owed across ${data.count} partner(s).`);
      else toast.message(data.reason === "nothing owed" ? "Nothing owed right now — no digest sent." : `Not sent: ${data.reason}`);
    } catch { toast.error("Could not send the payout digest."); }
  };

  const resetConnect = async (p) => {
    if (!window.confirm(`Disconnect ${p.partner_name}'s Stripe payout account? This clears the saved connection so they can reconnect from scratch. Your Stripe API keys are not affected.`)) return;
    try {
      await api.post(`/admin/referral-partners/${p.id}/connect/reset`);
      toast.success(`${p.partner_name}'s Stripe payout account disconnected — they can reconnect fresh.`);
      await load();
    } catch { toast.error("Could not disconnect the Stripe account."); }
  };

  const toggleAccess = async (p) => {
    const suspend = !p.access_suspended;
    if (suspend && !window.confirm(`Suspend ${p.partner_name}'s portal access? They won't be able to sign in, view documents, or download their statement until you restore it.`)) return;
    try {
      await api.post(`/admin/referral-partners/${p.id}/access`, { suspended: suspend });
      toast.success(suspend ? `${p.partner_name}'s portal access suspended.` : `${p.partner_name}'s portal access restored.`);
      await load();
    } catch { toast.error("Could not update portal access."); }
  };

  const resetAllConnect = async () => {
    if (!window.confirm("Disconnect the saved Stripe payout account for ALL partners so everyone reconnects from scratch? Your Stripe API keys are not affected.")) return;
    try {
      const { data } = await api.post("/admin/referral-connect/reset-all");
      toast.success(`Reset ${data.partners_reset} partner Stripe connection(s). Everyone can reconnect fresh.`);
      await load();
    } catch { toast.error("Could not reset the Stripe connections."); }
  };

  const save = async () => {
    const d = editing;
    if (!d.partner_name.trim()) return toast.error("Enter the partner's name or company.");
    setSaving(true);
    const payload = {
      ...d, revenue_pct: Number(d.revenue_pct) || 0,
      flat_fee_per_client: Number(d.flat_fee_per_client) || 0, term_months: Number(d.term_months) || 12,
      sales_pct: Number(d.sales_pct) || 0, upsell_pct: Number(d.upsell_pct) || 0,
      default_role: d.default_role || "referrer",
      rate_mode: d.rate_mode || "volume",
    };
    try {
      if (d.id) await api.patch(`/admin/referral-partners/${d.id}`, payload);
      else await api.post("/admin/referral-partners", payload);
      toast.success(d.id ? "Partner updated." : "Partner created.");
      setEditing(null); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const doSign = async () => {
    if (!signFor.signature_name?.trim()) return toast.error("Enter the signer's full legal name.");
    try {
      await api.post(`/admin/referral-partners/${signFor.id}/sign`, {
        signature_name: signFor.signature_name.trim(), signatory_title: signFor.signatory_title || undefined });
      toast.success("Agreement marked as signed."); setSignFor(null); await load();
    } catch { toast.error("Could not record signature."); }
  };

  const addReferral = async () => {
    if (!refFor.client_name?.trim()) return toast.error("Enter the referred client's name.");
    const mode = refFor.payout_type === "lump" ? "lump" : (refFor.payout_type === "onetime" ? "onetime" : "monthly");
    try {
      const body = {
        client_name: refFor.client_name.trim(),
        status: refFor.status || "referred",
        commission_pct: refFor.commission_pct === "" || refFor.commission_pct == null ? undefined : Number(refFor.commission_pct),
      };
      if (mode === "lump") {
        body.payout_type = "lump";
        body.lump_sum = Number(refFor.lump_sum) || 0;
        body.commission_pct = undefined;
      } else if (mode === "onetime") {
        body.payout_type = "percent";
        body.basis = refFor.basis || "contract_value";
        body.revenue_to_date = Number(refFor.revenue_to_date) || 0;
        body.flat_fee = refFor.flat_fee === "" || refFor.flat_fee == null ? undefined : Number(refFor.flat_fee);
      } else {
        body.payout_type = "percent";
        body.role = refFor.role || "referrer";
        body.monthly_payment = Number(refFor.monthly_payment) || 0;
        body.months_paid = Number(refFor.months_paid) || 0;
        if (refFor.role === "embedded") body.baseline_monthly = Number(refFor.baseline_monthly) || 0;
        if (refFor.role === "sales" || refFor.role === "embedded")
          body.sales_pct = refFor.sales_pct === "" || refFor.sales_pct == null ? undefined : Number(refFor.sales_pct);
        if (refFor.role === "embedded")
          body.upsell_pct = refFor.upsell_pct === "" || refFor.upsell_pct == null ? undefined : Number(refFor.upsell_pct);
      }
      const { data } = await api.post(`/admin/referral-partners/${refFor.id}/referrals`, body);
      toast.success("Deal logged.");
      if (data?._milestone) {
        toast.message(`Tier upgrade: ${data._milestone.partner_name} advanced to ${data._milestone.new_pct}% at client #${data._milestone.client_number}. Consider updating their terms.`, { duration: 8000 });
      }
      setRefFor(null); await load();
    } catch { toast.error("Could not log the deal."); }
  };

  const doDelete = async () => {
    try { await api.delete(`/admin/referral-partners/${delTarget.id}`); toast.success("Partner removed."); setDelTarget(null); await load(); }
    catch { toast.error("Delete failed."); }
  };

  const requoteDeals = async (p) => {
    if (!window.confirm(`Recompute all percentage deals for ${p.partner_name} using the current tier table? Lump-sum deals are unchanged.`)) return;
    try {
      const { data } = await api.post(`/admin/referral-partners/${p.id}/requote`);
      toast.success(data.changed ? `Updated ${data.changed} deal${data.changed === 1 ? "" : "s"} to current tiers.` : "All deals already match the current tiers.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not re-quote deals."); }
  };

  const sendForSignature = async (p) => {
    if (!p.contact_email) return toast.error("Add a contact email to this partner first (Edit).");
    setSendingId(p.id);
    try { const { data } = await api.post(`/admin/referral-partners/${p.id}/send-agreement`); toast.success("Agreement emailed with a click-to-sign link."); await load(); return data; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send."); }
    finally { setSendingId(null); }
  };

  const submitPayout = async () => {
    const amt = Number(payFor.amount);
    if (!amt || amt <= 0) return toast.error("Enter an amount greater than zero.");
    try {
      const { data } = await api.post(`/admin/referral-partners/${payFor.id}/payouts`, {
        amount: amt, mode: payFor.mode, method: payFor.method || undefined, note: payFor.note || undefined });
      const st = data.payout?.status;
      if (st === "pending_no_account") toast.message("Recorded as pending — partner hasn't finished Stripe onboarding.");
      else if (st === "failed") toast.error(`Stripe transfer failed: ${data.payout?.error || ""}`);
      else toast.success("Payout recorded.");
      setPayFor(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Payout failed."); }
  };

  if (partners === null) {
    return <div className="flex items-center gap-2 text-sm text-[#1e3d3a]/50 py-10 justify-center" data-testid="referrals-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading partners…</div>;
  }

  return (
    <div className="space-y-5" data-testid="referral-partners">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-[#1e3d3a] flex items-center gap-2"><Handshake className="h-5 w-5 text-[#c9a961]" /> Referral Partners</h2>
          <p className="text-sm text-[#1e3d3a]/55 mt-1">Commission-based referral & affiliate agreements — exclusivity, 1-year minimum term, and enforceable remedies. Generate a signed-ready PDF per partner.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="dl-template-individual" onClick={() => downloadPdf("/admin/referral-templates/individual/pdf", "Elevare_Referral_Template_Individual.pdf")} className={btnGhost}><FileDown className="h-3.5 w-3.5" /> Individual</button>
          <button data-testid="dl-template-company" onClick={() => downloadPdf("/admin/referral-templates/company/pdf", "Elevare_Referral_Template_Company.pdf")} className={btnGhost}><FileDown className="h-3.5 w-3.5" /> Company</button>
          <button data-testid="dl-template-small" onClick={() => downloadPdf("/admin/referral-templates/small/pdf", "Elevare_Referral_Template_Small_business.pdf")} className={btnGhost}><FileDown className="h-3.5 w-3.5" /> Small biz</button>
          <button data-testid="dl-template-medium" onClick={() => downloadPdf("/admin/referral-templates/medium/pdf", "Elevare_Referral_Template_Medium_business.pdf")} className={btnGhost}><FileDown className="h-3.5 w-3.5" /> Medium biz</button>
          <button data-testid="dl-template-large" onClick={() => downloadPdf("/admin/referral-templates/large/pdf", "Elevare_Referral_Template_Large_business.pdf")} className={btnGhost}><FileDown className="h-3.5 w-3.5" /> Large biz</button>
          <button data-testid="payout-digest-btn" onClick={sendPayoutDigest} className={btnGhost}><Mail className="h-3.5 w-3.5" /> Email payouts owed</button>
          <button data-testid="reset-all-stripe-btn" onClick={resetAllConnect} className={btnGhost}><Unplug className="h-3.5 w-3.5" /> Reset Stripe connections</button>
          <button data-testid="new-partner-btn" onClick={() => setEditing({ ...BLANK })} className={btnGold}><Plus className="h-4 w-4" /> New partner</button>
        </div>
      </div>

      <div className="rounded-xl border border-[#c9a961]/30 bg-[#c9a961]/5 px-4 py-3 text-[12px] text-[#8a6d2f] flex items-start gap-2" data-testid="referrals-legal-note">
        <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
        <span>These agreements are thorough templates, not legal advice. Have a licensed attorney in your governing state review and finalize before anyone signs.</span>
      </div>

      {milestones.length > 0 && (
        <div className="rounded-xl border border-[#4d5a24]/25 bg-[#4d5a24]/5 overflow-hidden" data-testid="milestone-center">
          <button onClick={() => setShowMilestones((v) => !v)} className="w-full flex items-center justify-between px-4 py-3 text-left">
            <span className="flex items-center gap-2 text-sm font-semibold text-[#3c4a1c]"><RefreshCcw className="h-4 w-4" /> Tier milestones ({milestones.length}) — partners who advanced a tier</span>
            <span className="text-[11px] text-[#3c4a1c]/60">{showMilestones ? "Hide" : "Show"}</span>
          </button>
          {showMilestones && (
            <div className="px-4 pb-3 space-y-1.5" data-testid="milestone-list">
              {milestones.slice(0, 12).map((m, i) => {
                const partner = partners.find((p) => p.id === m.partner_id);
                return (
                  <div key={i} className="flex items-center justify-between text-xs border-t border-[#4d5a24]/10 pt-1.5" data-testid={`milestone-${i}`}>
                    <span className="text-[#1e3d3a]"><b>{m.partner_name}</b> reached client #{m.client_number} · {m.old_pct}% → <b>{m.new_pct}%</b></span>
                    <span className="flex items-center gap-2">
                      <span className="text-[#1e3d3a]/40">{m.at ? new Date(m.at).toLocaleDateString() : ""}</span>
                      {partner && <button data-testid={`milestone-update-${i}`} onClick={() => setEditing({ ...BLANK, ...partner })} className="text-[#a3823f] hover:underline">Update terms</button>}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Partner leaderboard */}
      {partners.length > 0 && (
        <div className="rounded-xl border border-[#c9a961]/30 bg-[#c9a961]/5 overflow-hidden" data-testid="leaderboard">
          <button onClick={() => setShowBoard((v) => !v)} className="w-full flex items-center justify-between px-4 py-3 text-left">
            <span className="flex items-center gap-2 text-sm font-semibold text-[#8a6d2f]"><Trophy className="h-4 w-4" /> Partner leaderboard — top performers</span>
            <span className="text-[11px] text-[#8a6d2f]/70">{showBoard ? "Hide" : "Show"}</span>
          </button>
          {showBoard && (
            <div className="px-4 pb-4" data-testid="leaderboard-body">
              <div className="flex items-center gap-2 mb-2 text-[11px]">
                <span className="text-[#1e3d3a]/50 uppercase tracking-wide">Sort by</span>
                <button data-testid="board-sort-mrr" onClick={() => setBoardSort("mrr")} className={`rounded-full px-2.5 py-0.5 font-medium ${boardSort === "mrr" ? "bg-[#1e3d3a] text-[#c9a961]" : "bg-[#1e3d3a]/8 text-[#1e3d3a]/60"}`}>MRR</button>
                <button data-testid="board-sort-clients" onClick={() => setBoardSort("clients")} className={`rounded-full px-2.5 py-0.5 font-medium ${boardSort === "clients" ? "bg-[#1e3d3a] text-[#c9a961]" : "bg-[#1e3d3a]/8 text-[#1e3d3a]/60"}`}>Clients signed</button>
              </div>
              <div className="space-y-1">
                {[...partners].sort((a, b) => boardSort === "clients" ? (b.clients_signed || 0) - (a.clients_signed || 0) || (b.mrr || 0) - (a.mrr || 0) : (b.mrr || 0) - (a.mrr || 0) || (b.clients_signed || 0) - (a.clients_signed || 0)).slice(0, 10).map((p, i) => (
                  <div key={p.id} className="flex items-center gap-3 rounded-lg bg-white/70 px-3 py-2 text-xs" data-testid={`board-row-${i}`}>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${i === 0 ? "bg-[#c9a961] text-white" : i < 3 ? "bg-[#c9a961]/25 text-[#8a6d2f]" : "bg-[#1e3d3a]/8 text-[#1e3d3a]/50"}`}>{i + 1}</span>
                    <span className="flex-1 min-w-0 truncate font-medium text-[#1e3d3a]">{p.partner_name}</span>
                    <span className="tabular-nums text-[#8a6d2f] font-semibold" title="Monthly recurring commission">{money(p.mrr)}/mo</span>
                    <span className="tabular-nums text-[#1e3d3a]/60 w-16 text-right">{p.clients_signed || 0} signed</span>
                    <span className="tabular-nums text-[#4d5a24] w-20 text-right" title="Earned">{money(p.commission_earned)}</span>
                    <span className="tabular-nums text-[#a3823f] w-20 text-right" title="Owed">{money(p.commission_owed)} owed</span>
                    {(p.dropped_count > 0 || p.chargeback_count > 0) && (
                      <span className="flex items-center gap-1 text-[10px] text-[#b23a48]"><AlertTriangle className="h-3 w-3" />{(p.dropped_count || 0) + (p.chargeback_count || 0)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Retention risk */}
      {retention.total > 0 && (
        <div className="rounded-xl border border-[#b23a48]/25 bg-[#b23a48]/5 overflow-hidden" data-testid="retention-panel">
          <button onClick={() => setShowRetention((v) => !v)} className="w-full flex items-center justify-between px-4 py-3 text-left">
            <span className="flex items-center gap-2 text-sm font-semibold text-[#b23a48]"><AlertTriangle className="h-4 w-4" /> Retention risk ({retention.total}) — clients at risk of losing commission</span>
            <span className="text-[11px] text-[#b23a48]/70">{showRetention ? "Hide" : "Show"}</span>
          </button>
          {showRetention && (
            <div className="px-4 pb-3 space-y-1.5" data-testid="retention-list">
              {retention.alerts.map((a, i) => (
                <div key={i} className="flex items-center justify-between gap-3 text-xs border-t border-[#b23a48]/10 pt-1.5" data-testid={`retention-${i}`}>
                  <span className="min-w-0">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase mr-2 ${a.severity === "high" ? "bg-[#b23a48]/15 text-[#b23a48]" : "bg-[#c98a3f]/15 text-[#a3823f]"}`}>{a.reason}</span>
                    <b className="text-[#1e3d3a]">{a.client_name}</b> <span className="text-[#1e3d3a]/50">· {a.partner_name}</span> — {a.detail}
                  </span>
                  <span className="text-[#1e3d3a]/40 whitespace-nowrap">{a.at ? new Date(a.at).toLocaleDateString() : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Monthly payout run */}
      {partners.length > 0 && (
        <div className="rounded-xl border border-[#1e3d3a]/15 bg-[#1e3d3a]/5 overflow-hidden" data-testid="payout-run-panel">
          <button onClick={() => setShowPayouts((v) => !v)} className="w-full flex items-center justify-between px-4 py-3 text-left">
            <span className="flex items-center gap-2 text-sm font-semibold text-[#1e3d3a]"><Banknote className="h-4 w-4 text-[#c9a961]" /> Monthly payout run — batch & pay commissions owed</span>
            <span className="text-[11px] text-[#1e3d3a]/60">{showPayouts ? "Hide" : "Show"}</span>
          </button>
          {showPayouts && (
            <div className="px-4 pb-4 space-y-3" data-testid="payout-run-body">
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="flex items-center gap-1.5 text-[#1e3d3a]/60"><CalendarClock className="h-3.5 w-3.5" /> Auto-prepare on day</span>
                <input data-testid="schedule-day" type="number" min="1" max="28" value={schedule.payout_day} onChange={(e) => setSchedule((s) => ({ ...s, payout_day: Number(e.target.value) }))} onBlur={(e) => saveSchedule({ payout_day: Math.max(1, Math.min(28, Number(e.target.value) || 1)) })} className="w-16 rounded-md border border-[#1e3d3a]/15 px-2 py-1" />
                <span className="text-[#1e3d3a]/40">of each month</span>
                <label className="flex items-center gap-2 cursor-pointer text-[#1e3d3a]/75">
                  <input data-testid="schedule-autosend" type="checkbox" checked={!!schedule.auto_send} onChange={(e) => saveSchedule({ auto_send: e.target.checked })} />
                  Auto-send on that day (otherwise we email you to review)
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="prepare-batch-btn" onClick={prepareBatch} disabled={batchBusy} className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-4 py-2 text-xs font-medium text-[#1e3d3a] hover:border-[#c9a961] disabled:opacity-50">{batchBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />} Prepare batch now</button>
                {batch && batch.status !== "completed" && batch.ready_count > 0 && (
                  <button data-testid="run-batch-btn" onClick={runBatch} disabled={batchBusy} className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-xs font-semibold text-white hover:bg-[#b8974e] disabled:opacity-50"><Play className="h-3.5 w-3.5" /> Pay all ({batch.ready_count}) — {money(batch.ready_total)}</button>
                )}
              </div>
              {batch && (
                <div className="rounded-lg border border-[#1e3d3a]/10 bg-white overflow-hidden" data-testid="batch-detail">
                  <div className="flex items-center justify-between px-3 py-2 bg-[#1e3d3a]/5 text-[11px] text-[#1e3d3a]/60">
                    <span>Batch {batch.id} · {batch.status}{batch.status === "completed" ? ` · paid ${money(batch.paid_total)}` : ""}</span>
                    <span>{batch.count} owed · total {money(batch.total)}</span>
                  </div>
                  <div className="divide-y divide-[#1e3d3a]/8">
                    {(batch.entries || []).map((e, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-xs" data-testid={`batch-entry-${i}`}>
                        <span className="text-[#1e3d3a] font-medium">{e.partner_name}</span>
                        <span className="flex items-center gap-3">
                          <span className="tabular-nums text-[#a3823f] font-semibold">{money(e.owed || e.amount)}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase ${e.status === "paid" ? "bg-[#4d5a24]/15 text-[#4d5a24]" : e.status === "failed" ? "bg-[#b23a48]/15 text-[#b23a48]" : e.connect_ready === false || e.status === "pending" ? "bg-[#c98a3f]/15 text-[#a3823f]" : "bg-[#1e3d3a]/8 text-[#1e3d3a]/55"}`}>
                            {e.status === "pending" || e.connect_ready === false ? "no stripe" : e.status}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}


      {partners.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#1e3d3a]/20 py-12 text-center" data-testid="referrals-empty">
          <Handshake className="h-8 w-8 text-[#1e3d3a]/20 mx-auto" />
          <p className="mt-3 text-sm text-[#1e3d3a]/60">No referral partners yet. Create one to generate a personalized agreement and track their referrals.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#1e3d3a]/10">
          <table className="w-full text-sm" data-testid="referrals-table">
            <thead className="bg-[#1e3d3a]/5 text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/50">
              <tr>
                <th className="px-4 py-3 text-left">Partner</th>
                <th className="px-4 py-3 text-left">Terms</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Referrals</th>
                <th className="px-4 py-3 text-right">Revenue</th>
                <th className="px-4 py-3 text-right">Commission</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1e3d3a]/8">
              {partners.map((p) => (
                <Fragment key={p.id}>
                <tr className="hover:bg-[#c9a961]/5 transition-colors" data-testid={`partner-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 font-medium text-[#1e3d3a]">
                      <button data-testid={`expand-partner-${p.id}`} onClick={() => toggleExpand(p.id)} className="p-0.5 rounded hover:bg-[#1e3d3a]/8 text-[#1e3d3a]/50">
                        {expanded[p.id] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </button>
                      {p.partner_type === "company" ? <Building2 className="h-3.5 w-3.5 text-[#a3823f]" /> : <User className="h-3.5 w-3.5 text-[#a3823f]" />}
                      {p.partner_name}
                    </div>
                    {p.contact_email && <div className="text-xs text-[#1e3d3a]/45 pl-6">{p.contact_email}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#1e3d3a]/70">
                    <div>{p.revenue_pct}% · {DURATION[p.commission_duration] || p.commission_duration}</div>
                    <div className="text-[#1e3d3a]/45">{EXCL[p.exclusivity_mode]} · {p.term_months}mo term</div>
                  </td>
                  <td className="px-4 py-3"><span className={`inline-block rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${STATUS_COLORS[p.status] || STATUS_COLORS.draft}`}>{p.status}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <div className="font-medium text-[#1e3d3a]">{p.clients_signed ?? p.referral_count} signed</div>
                    <div className="flex items-center justify-end gap-1.5 mt-0.5 text-[10px]">
                      {p.paid_count > 0 && <span className="text-[#8a6d2f]">{p.paid_count} paid</span>}
                      {p.unpaid_count > 0 && <span className="text-[#a3823f]">{p.unpaid_count} unpaid</span>}
                      {p.dropped_count > 0 && <span className="text-[#6b6b6b]">{p.dropped_count} dropped</span>}
                      {p.chargeback_count > 0 && <span className="text-[#b23a48]">{p.chargeback_count} CB</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{money(p.revenue_total)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <div className="font-semibold text-[#4d5a24]">{money(p.commission_earned)} <span className="text-[9px] font-normal text-[#1e3d3a]/40 uppercase">earned</span></div>
                    {p.expected_future > 0 && <div className="text-[10px] text-[#a3823f]">+{money(p.expected_future)} expected</div>}
                    {p.chargeback_amount > 0 && <div className="text-[10px] text-[#b23a48]">−{money(p.chargeback_amount)} clawed back</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button title="Download agreement PDF" data-testid={`dl-agreement-${p.id}`} onClick={() => downloadPdf(`/admin/referral-partners/${p.id}/agreement-pdf`, `Elevare_Referral_Agreement_${(p.partner_name||"partner").replace(/[^A-Za-z0-9_-]+/g,"_")}.pdf`)} className="p-1.5 rounded-lg text-[#1e3d3a]/60 hover:text-[#a3823f] hover:bg-[#c9a961]/10"><FileDown className="h-4 w-4" /></button>
                      <button title="Email for signature" data-testid={`send-agreement-${p.id}`} onClick={() => sendForSignature(p)} disabled={sendingId === p.id} className="p-1.5 rounded-lg text-[#1e3d3a]/60 hover:text-[#a3823f] hover:bg-[#c9a961]/10 disabled:opacity-40">{sendingId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}</button>
                      <button title="Payouts" data-testid={`payouts-${p.id}`} onClick={() => setPayFor({ id: p.id, name: p.partner_name, earned: p.commission_earned, paid: p.commission_paid, owed: p.commission_owed, connect_ready: p.connect_ready, amount: p.commission_owed > 0 ? p.commission_owed : "", mode: p.connect_ready ? "stripe" : "manual", method: "", note: "" })} className="p-1.5 rounded-lg text-[#1e3d3a]/60 hover:text-[#4d5a24] hover:bg-[#4d5a24]/10"><Wallet className="h-4 w-4" /></button>
                      {p.connect_started && <button title="Disconnect Stripe payout account (reconnect from scratch)" data-testid={`reset-connect-${p.id}`} onClick={() => resetConnect(p)} className="p-1.5 rounded-lg text-[#1e3d3a]/60 hover:text-[#b23a48] hover:bg-[#b23a48]/10"><Unplug className="h-4 w-4" /></button>}
                      <button title="Re-quote deals to current tiers" data-testid={`requote-${p.id}`} onClick={() => requoteDeals(p)} className="p-1.5 rounded-lg text-[#1e3d3a]/60 hover:text-[#a3823f] hover:bg-[#c9a961]/10"><RefreshCcw className="h-4 w-4" /></button>
                      <button title="Log a deal" data-testid={`add-referral-${p.id}`} onClick={() => setRefFor({ id: p.id, client_name: "", revenue_to_date: "", status: "active", payout_type: "monthly", basis: p.payout_basis || "contract_value", lump_sum: "", commission_pct: "", flat_fee: p.flat_fee_per_client, tiers: p.tiers, cap: p.tier_cap_pct || 35, clientNumber: (p.referral_count || 0) + 1, role: p.default_role || "referrer", monthly_payment: "", baseline_monthly: "", months_paid: 0, sales_pct: p.sales_pct ?? 5, upsell_pct: p.upsell_pct ?? 10, duration: p.commission_duration || "12_months", spendTiers: p.spend_tiers, rateMode: p.rate_mode || "volume" })} className="p-1.5 rounded-lg text-[#1e3d3a]/60 hover:text-[#a3823f] hover:bg-[#c9a961]/10"><UserPlus className="h-4 w-4" /></button>
                      <button title="Mark signed" data-testid={`sign-${p.id}`} onClick={() => setSignFor({ id: p.id, signature_name: p.partner_type === "company" ? (p.signatory_name || "") : p.partner_name, signatory_title: p.signatory_title || "" })} className="p-1.5 rounded-lg text-[#1e3d3a]/60 hover:text-[#4d5a24] hover:bg-[#4d5a24]/10"><PenLine className="h-4 w-4" /></button>
                      <button title={p.access_suspended ? "Restore portal access" : "Suspend portal access"} data-testid={`toggle-access-${p.id}`} onClick={() => toggleAccess(p)} className={`p-1.5 rounded-lg ${p.access_suspended ? "text-[#b23a48] bg-[#b23a48]/10 hover:bg-[#b23a48]/20" : "text-[#1e3d3a]/60 hover:text-[#b23a48] hover:bg-[#b23a48]/10"}`}>{p.access_suspended ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />}</button>
                      <button title="Edit" data-testid={`edit-${p.id}`} onClick={() => setEditing({ ...BLANK, ...p })} className="p-1.5 rounded-lg text-[#1e3d3a]/60 hover:text-[#1e3d3a] hover:bg-[#1e3d3a]/8"><Pencil className="h-4 w-4" /></button>
                      <button title="Delete" data-testid={`delete-${p.id}`} onClick={() => setDelTarget(p)} className="p-1.5 rounded-lg text-[#1e3d3a]/60 hover:text-[#b23a48] hover:bg-[#b23a48]/10"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
                {expanded[p.id] && (
                  <tr data-testid={`partner-deals-${p.id}`} className="bg-[#faf8f2]">
                    <td colSpan={7} className="px-4 py-4">
                      <div className="rounded-xl border border-[#1e3d3a]/10 bg-white overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#1e3d3a]/5">
                          <span className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50">Deals & client lifecycle · current tier {p.current_tier_pct ?? p.revenue_pct}%</span>
                          <span className="text-[10px] text-[#1e3d3a]/45">Change a status to move a client through the funnel</span>
                        </div>
                        {(p.referrals || []).length === 0 ? (
                          <p className="px-4 py-5 text-sm text-[#1e3d3a]/45 text-center">No deals logged yet. Use the <UserPlus className="inline h-3.5 w-3.5" /> button to log one.</p>
                        ) : (
                          <div className="divide-y divide-[#1e3d3a]/8">
                            {(p.referrals || []).map((r) => (
                              <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5" data-testid={`deal-row-${r.id}`}>
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm text-[#1e3d3a] font-medium truncate">{r.client_name}</div>
                                  <div className="text-[11px] text-[#1e3d3a]/45">
                                    {r.payout_type === "lump"
                                      ? "Lump sum"
                                      : r.monthly_payment != null
                                        ? `${ROLE_LABEL[r.role || p.default_role] || "Referrer"} · ${money(r.monthly_payment)}/mo · ${r.months_paid || 0} mo paid`
                                        : `${r.commission_pct ?? p.revenue_pct}% of ${money(r.revenue_to_date)}`}
                                    {r.client_number ? ` · client #${r.client_number}` : ""}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm font-semibold text-[#a3823f] tabular-nums">{money2(dealPayout(r, p))}</div>
                                  <div className="text-[10px] text-[#1e3d3a]/40 tabular-nums">{r.monthly_payment != null ? "projected" : `${money(r.revenue_to_date)} deal`}</div>
                                </div>
                                <select
                                  data-testid={`deal-status-${r.id}`}
                                  value={r.status || "referred"}
                                  onChange={(e) => updateDealStatus(p.id, r.id, e.target.value)}
                                  className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#c9a961]/30 ${REF_STATUS_COLORS[r.status] || REF_STATUS_COLORS.referred}`}>
                                  {REF_STATUSES.map((s) => <option key={s} value={s}>{REF_STATUS_LABEL[s]}</option>)}
                                </select>
                                <button title="Remove deal" data-testid={`deal-delete-${r.id}`} onClick={() => deleteDeal(p.id, r.id)} className="p-1.5 rounded-lg text-[#1e3d3a]/50 hover:text-[#b23a48] hover:bg-[#b23a48]/10"><Trash2 className="h-3.5 w-3.5" /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setEditing(null)} data-testid="partner-editor">
          <div className="h-full w-full max-w-lg overflow-y-auto bg-[#faf8f2] shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-[#1e3d3a]">{editing.id ? "Edit partner" : "New referral partner"}</h3>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded-lg hover:bg-[#1e3d3a]/8"><X className="h-4 w-4 text-[#1e3d3a]" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Partner type</label>
                <div className="flex gap-2">
                  {["individual", "company"].map((t) => (
                    <button key={t} data-testid={`ptype-${t}`} onClick={() => setEditing({ ...editing, partner_type: t })} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium capitalize transition-colors ${editing.partner_type === t ? "border-[#c9a961] bg-[#c9a961]/10 text-[#a3823f]" : "border-[#1e3d3a]/15 text-[#1e3d3a]/60"}`}>{t === "company" ? "Company / Affiliate" : "Individual"}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls}>{editing.partner_type === "company" ? "Company legal name" : "Partner full name"}</label>
                <input data-testid="f-partner-name" className={inputCls} value={editing.partner_name} onChange={(e) => setEditing({ ...editing, partner_name: e.target.value })} placeholder={editing.partner_type === "company" ? "Acme Referrals LLC" : "Jane Doe"} />
              </div>
              {editing.partner_type === "company" && (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Authorized signatory</label><input data-testid="f-signatory-name" className={inputCls} value={editing.signatory_name} onChange={(e) => setEditing({ ...editing, signatory_name: e.target.value })} placeholder="John Smith" /></div>
                  <div><label className={labelCls}>Signatory title</label><input data-testid="f-signatory-title" className={inputCls} value={editing.signatory_title} onChange={(e) => setEditing({ ...editing, signatory_title: e.target.value })} placeholder="Managing Member" /></div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Contact email</label><input data-testid="f-email" className={inputCls} value={editing.contact_email} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} /></div>
                <div><label className={labelCls}>Contact phone</label><input data-testid="f-phone" className={inputCls} value={editing.contact_phone} onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })} /></div>
              </div>
              <div><label className={labelCls}>Partner address</label><input data-testid="f-partner-address" className={inputCls} value={editing.partner_address} onChange={(e) => setEditing({ ...editing, partner_address: e.target.value })} placeholder="123 Main St, City, ST" /></div>

              <div className="border-t border-[#1e3d3a]/10 pt-4 grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Company name</label><input data-testid="f-company-name" className={inputCls} value={editing.company_name} onChange={(e) => setEditing({ ...editing, company_name: e.target.value })} /></div>
                <div><label className={labelCls}>Governing state</label><input data-testid="f-state" className={inputCls} value={editing.governing_state} onChange={(e) => setEditing({ ...editing, governing_state: e.target.value })} placeholder="Texas" /></div>
              </div>
              <div><label className={labelCls}>Company address</label><input data-testid="f-company-address" className={inputCls} value={editing.company_address} onChange={(e) => setEditing({ ...editing, company_address: e.target.value })} placeholder="500 Congress Ave, Austin, TX" /></div>

              <div className="border-t border-[#1e3d3a]/10 pt-4 grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Business scale</label>
                  <select data-testid="f-scale" className={inputCls} value={editing.business_scale || "medium"} onChange={(e) => {
                    const sc = e.target.value; const d = SCALE_DEFAULTS[sc];
                    setEditing({ ...editing, business_scale: sc, tiers: d.tiers.map((t) => ({ ...t })), revenue_pct: d.pct });
                  }}>
                    {Object.entries(SCALE_DEFAULTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Payout based on</label>
                  <select data-testid="f-basis" className={inputCls} value={editing.payout_basis || "contract_value"} onChange={(e) => setEditing({ ...editing, payout_basis: e.target.value })}>
                    <option value="contract_value">What client pays Elevare</option>
                    <option value="client_sales">Client's sales / revenue</option>
                  </select>
                </div>
              </div>

              <div className="rounded-xl border border-[#1e3d3a]/12 p-3" data-testid="f-tiers">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50">Volume tiers — % rises with clients brought</span>
                  <button type="button" data-testid="f-tier-add" onClick={() => setEditing({ ...editing, tiers: [...(editing.tiers || []), { min_clients: 1, pct: 10 }] })} className="text-[#a3823f] hover:text-[#1e3d3a]"><Plus className="h-3.5 w-3.5" /></button>
                </div>
                <div className="space-y-1.5">
                  {(editing.tiers || []).map((t, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs" data-testid={`f-tier-row-${i}`}>
                      <span className="text-[#1e3d3a]/50">From</span>
                      <input type="number" data-testid={`f-tier-min-${i}`} className="w-16 rounded-md border border-[#1e3d3a]/15 px-2 py-1" value={t.min_clients} onChange={(e) => { const ts = [...editing.tiers]; ts[i] = { ...ts[i], min_clients: Number(e.target.value) }; setEditing({ ...editing, tiers: ts }); }} />
                      <span className="text-[#1e3d3a]/50">clients →</span>
                      <input type="number" data-testid={`f-tier-pct-${i}`} className="w-16 rounded-md border border-[#1e3d3a]/15 px-2 py-1" value={t.pct} onChange={(e) => { const ts = [...editing.tiers]; ts[i] = { ...ts[i], pct: Number(e.target.value) }; setEditing({ ...editing, tiers: ts }); }} />
                      <span className="text-[#1e3d3a]/50">%</span>
                      <button type="button" onClick={() => setEditing({ ...editing, tiers: editing.tiers.filter((_, j) => j !== i) })} className="ml-auto text-[#1e3d3a]/40 hover:text-[#b23a48]"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/50">Max cap %</span>
                  <input type="number" data-testid="f-cap" className="w-20 rounded-md border border-[#1e3d3a]/15 px-2 py-1 text-xs" value={editing.tier_cap_pct ?? 35} onChange={(e) => setEditing({ ...editing, tier_cap_pct: Number(e.target.value) })} />
                  <span className="text-[10px] text-[#1e3d3a]/40">payout never exceeds this</span>
                </div>
                <label className="mt-3 flex items-center gap-2 text-xs text-[#1e3d3a]/75 cursor-pointer">
                  <input type="checkbox" data-testid="f-auto-requote" checked={!!editing.auto_requote} onChange={(e) => setEditing({ ...editing, auto_requote: e.target.checked })} />
                  Auto re-quote this partner's open deals whenever I change these tiers
                </label>
              </div>

              <div className="rounded-xl border border-[#1e3d3a]/12 p-3" data-testid="f-rate-mode-box">
                <label className={labelCls}>How to set each client's referral %</label>
                <select data-testid="f-rate-mode" className={inputCls} value={editing.rate_mode || "volume"} onChange={(e) => setEditing({ ...editing, rate_mode: e.target.value })}>
                  {RATE_MODES.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                </select>
                {(editing.rate_mode === "size" || editing.rate_mode === "higher") && (
                  <div className="mt-3" data-testid="f-spend-tiers">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50">Client-size rate card — % by monthly spend</span>
                      <button type="button" data-testid="f-spend-add" onClick={() => setEditing({ ...editing, spend_tiers: [...(editing.spend_tiers || []), { min_monthly: 0, pct: 10 }] })} className="text-[#a3823f] hover:text-[#1e3d3a]"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="space-y-1.5">
                      {(editing.spend_tiers || []).map((t, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs" data-testid={`f-spend-row-${i}`}>
                          <span className="text-[#1e3d3a]/50">$</span>
                          <input type="number" data-testid={`f-spend-min-${i}`} className="w-24 rounded-md border border-[#1e3d3a]/15 px-2 py-1" value={t.min_monthly} onChange={(e) => { const ts = [...editing.spend_tiers]; ts[i] = { ...ts[i], min_monthly: Number(e.target.value) }; setEditing({ ...editing, spend_tiers: ts }); }} />
                          <span className="text-[#1e3d3a]/50">+/mo →</span>
                          <input type="number" data-testid={`f-spend-pct-${i}`} className="w-16 rounded-md border border-[#1e3d3a]/15 px-2 py-1" value={t.pct} onChange={(e) => { const ts = [...editing.spend_tiers]; ts[i] = { ...ts[i], pct: Number(e.target.value) }; setEditing({ ...editing, spend_tiers: ts }); }} />
                          <span className="text-[#1e3d3a]/50">%</span>
                          <button type="button" onClick={() => setEditing({ ...editing, spend_tiers: editing.spend_tiers.filter((_, j) => j !== i) })} className="ml-auto text-[#1e3d3a]/40 hover:text-[#b23a48]"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-[#1e3d3a]/40 mt-2">Bigger clients earn a higher %. Capped at your max above.</p>
                  </div>
                )}
              </div>


              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Base commission % (fallback)</label><input data-testid="f-pct" type="number" className={inputCls} value={editing.revenue_pct} onChange={(e) => setEditing({ ...editing, revenue_pct: e.target.value })} /></div>
                <div>
                  <label className={labelCls}>Commission duration</label>
                  <select data-testid="f-duration" className={inputCls} value={editing.commission_duration} onChange={(e) => setEditing({ ...editing, commission_duration: e.target.value })}>
                    {Object.entries(DURATION).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div><label className={labelCls}>Flat fee per client ($)</label><input data-testid="f-flat" type="number" className={inputCls} value={editing.flat_fee_per_client} onChange={(e) => setEditing({ ...editing, flat_fee_per_client: e.target.value })} /></div>
                <div><label className={labelCls}>Term (months)</label><input data-testid="f-term" type="number" className={inputCls} value={editing.term_months} onChange={(e) => setEditing({ ...editing, term_months: e.target.value })} /></div>
              </div>

              <div className="rounded-xl border border-[#1e3d3a]/12 p-3 space-y-3" data-testid="f-roles">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50">Compensation roles — commission is a % of the client's monthly payment</span>
                <div>
                  <label className={labelCls}>Default role for new deals</label>
                  <select data-testid="f-default-role" className={inputCls} value={editing.default_role || "referrer"} onChange={(e) => setEditing({ ...editing, default_role: e.target.value })}>
                    {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label} — {r.hint}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Closing % (sales)</label><input data-testid="f-sales-pct" type="number" className={inputCls} value={editing.sales_pct} onChange={(e) => setEditing({ ...editing, sales_pct: e.target.value })} placeholder="5" /><p className="text-[10px] text-[#1e3d3a]/45 mt-1">Extra % for closing the deal.</p></div>
                  <div><label className={labelCls}>Growth % (upsell)</label><input data-testid="f-upsell-pct" type="number" className={inputCls} value={editing.upsell_pct} onChange={(e) => setEditing({ ...editing, upsell_pct: e.target.value })} placeholder="10" /><p className="text-[10px] text-[#1e3d3a]/45 mt-1">% on the growth in a client's monthly spend (embedded).</p></div>
                </div>
              </div>
              <div>
                <label className={labelCls}>Exclusivity</label>
                <select data-testid="f-exclusivity" className={inputCls} value={editing.exclusivity_mode} onChange={(e) => setEditing({ ...editing, exclusivity_mode: e.target.value })}>
                  {Object.entries(EXCL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Payout terms</label><input data-testid="f-payout" className={inputCls} value={editing.payout_terms} onChange={(e) => setEditing({ ...editing, payout_terms: e.target.value })} /></div>
              <div><label className={labelCls}>Liquidated damages (optional)</label><input data-testid="f-liquidated" className={inputCls} value={editing.liquidated_damages} onChange={(e) => setEditing({ ...editing, liquidated_damages: e.target.value })} placeholder="Leave blank for default ($10,000 or 24-mo commissions)" /></div>
              <div><label className={labelCls}>Effective date (optional)</label><input data-testid="f-effective" className={inputCls} value={editing.effective_date} onChange={(e) => setEditing({ ...editing, effective_date: e.target.value })} placeholder="Defaults to today" /></div>

              <div className="flex gap-2 pt-2">
                <button data-testid="save-partner-btn" onClick={save} disabled={saving} className={btnGold}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save partner</button>
                <button onClick={() => setEditing(null)} className={btnGhost}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sign modal */}
      {signFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSignFor(null)} data-testid="sign-modal">
          <div className="w-full max-w-md rounded-2xl bg-[#faf8f2] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-[#1e3d3a] mb-1">Record signature</h3>
            <p className="text-xs text-[#1e3d3a]/55 mb-4">Enter the signer's full legal name to mark this agreement as signed (E-SIGN compliant).</p>
            <label className={labelCls}>Signer full legal name</label>
            <input data-testid="sign-name-input" className={inputCls} value={signFor.signature_name} onChange={(e) => setSignFor({ ...signFor, signature_name: e.target.value })} />
            <div className="flex gap-2 mt-4">
              <button data-testid="confirm-sign-btn" onClick={doSign} className={btnGold}><PenLine className="h-4 w-4" /> Mark signed</button>
              <button onClick={() => setSignFor(null)} className={btnGhost}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add referral modal */}
      {refFor && (() => {
        const base = Number(refFor.revenue_to_date) || 0;
        const cap = refFor.cap || 35;
        const n = Number(refFor.clientNumber) || 1;
        const modeIsMonthly = refFor.payout_type !== "lump" && refFor.payout_type !== "onetime";
        const tier = effectiveRefPct({ tiers: refFor.tiers, spendTiers: refFor.spendTiers, rateMode: refFor.rateMode, n, monthly: modeIsMonthly ? refFor.monthly_payment : null, cap });
        const override = refFor.commission_pct === "" || refFor.commission_pct == null ? null : Number(refFor.commission_pct);
        const effPct = Math.min(override == null ? tier : override, cap);
        const flat = Number(refFor.flat_fee) || 0;
        const pctPayout = base * effPct / 100 + flat;
        const dealScale = scaleFor(base);
        const lumpSuggested = SCALE_DEFAULTS[dealScale].lump;
        const mode = refFor.payout_type === "lump" ? "lump" : (refFor.payout_type === "onetime" ? "onetime" : "monthly");
        const role = refFor.role || "referrer";
        const durMonths = DURATION_MONTHS[refFor.duration] || 12;
        const mc = monthlyCommission({ monthly: refFor.monthly_payment, role, refPct: effPct, salesPct: refFor.sales_pct, upsellPct: refFor.upsell_pct, baseline: refFor.baseline_monthly });
        const projected = mc * durMonths;
        const setMode = (m) => setRefFor({ ...refFor, payout_type: m === "lump" ? "lump" : m, lump_sum: m === "lump" ? (refFor.lump_sum || lumpSuggested) : refFor.lump_sum });
        const modeBtn = (m, label) => (
          <button data-testid={`ref-mode-${m}`} onClick={() => setMode(m)} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${mode === m ? "border-[#c9a961] bg-[#c9a961]/10 text-[#a3823f]" : "border-[#1e3d3a]/15 text-[#1e3d3a]/60"}`}>{label}</button>
        );
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setRefFor(null)} data-testid="referral-modal">
          <div className="w-full max-w-lg rounded-2xl bg-[#faf8f2] p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-[#1e3d3a] mb-1">Log a deal & payout</h3>
            <p className="text-xs text-[#1e3d3a]/55 mb-4">Client #{n} for this partner</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Client / company name</label><input data-testid="ref-client-input" className={inputCls} value={refFor.client_name} onChange={(e) => setRefFor({ ...refFor, client_name: e.target.value })} /></div>
                <div><label className={labelCls}>This is client #</label><input data-testid="ref-clientnum-input" type="number" className={inputCls} value={refFor.clientNumber} onChange={(e) => setRefFor({ ...refFor, clientNumber: e.target.value })} /></div>
              </div>

              <div>
                <label className={labelCls}>Deal type</label>
                <div className="flex gap-2">
                  {modeBtn("monthly", "Recurring monthly")}
                  {modeBtn("onetime", "One-time %")}
                  {modeBtn("lump", "Lump sum")}
                </div>
              </div>

              {mode === "monthly" && (
                <>
                  <div>
                    <label className={labelCls}>Partner role on this deal</label>
                    <select data-testid="ref-role-select" className={inputCls} value={role} onChange={(e) => setRefFor({ ...refFor, role: e.target.value })}>
                      {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label} — {r.hint}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={labelCls}>Client pays Elevare / month ($)</label><input data-testid="ref-monthly-input" type="number" className={inputCls} value={refFor.monthly_payment} onChange={(e) => setRefFor({ ...refFor, monthly_payment: e.target.value })} placeholder="0" /></div>
                    <div><label className={labelCls}>Months paid so far</label><input data-testid="ref-months-input" type="number" className={inputCls} value={refFor.months_paid} onChange={(e) => setRefFor({ ...refFor, months_paid: e.target.value })} placeholder="0" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Referral % (auto: {tier}%)</label>
                      <input data-testid="ref-pct-input" type="number" className={inputCls} value={refFor.commission_pct ?? ""} onChange={(e) => setRefFor({ ...refFor, commission_pct: e.target.value })} placeholder={`${tier}`} />
                    </div>
                    {(role === "sales" || role === "embedded") && (
                      <div><label className={labelCls}>Closing % (sales)</label><input data-testid="ref-sales-input" type="number" className={inputCls} value={refFor.sales_pct ?? ""} onChange={(e) => setRefFor({ ...refFor, sales_pct: e.target.value })} placeholder="5" /></div>
                    )}
                  </div>
                  {role === "embedded" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={labelCls}>Starting monthly ($)</label><input data-testid="ref-baseline-input" type="number" className={inputCls} value={refFor.baseline_monthly} onChange={(e) => setRefFor({ ...refFor, baseline_monthly: e.target.value })} placeholder="0" /></div>
                      <div><label className={labelCls}>Growth % (upsell)</label><input data-testid="ref-upsell-input" type="number" className={inputCls} value={refFor.upsell_pct ?? ""} onChange={(e) => setRefFor({ ...refFor, upsell_pct: e.target.value })} placeholder="10" /></div>
                    </div>
                  )}
                  <div className="rounded-xl bg-[#1e3d3a]/5 p-3" data-testid="ref-monthly-preview">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">Monthly commission</div>
                        <div className="text-2xl font-semibold text-[#a3823f] tabular-nums">${mc.toLocaleString(undefined, { maximumFractionDigits: 2 })}<span className="text-xs font-normal text-[#1e3d3a]/45">/mo</span></div>
                      </div>
                      <div className="text-right text-[11px] text-[#1e3d3a]/55 leading-relaxed">
                        <div>Projected over {durMonths} mo: <b className="text-[#1e3d3a]">${projected.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b></div>
                        <div className="text-[10px]">{effPct}% referral{(role === "sales" || role === "embedded") ? ` + ${Number(refFor.sales_pct) || 0}% closing` : ""}{role === "embedded" ? ` + ${Number(refFor.upsell_pct) || 0}% growth` : ""}</div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {mode === "onetime" && (
                <>
                  <div>
                    <label className={labelCls}>Base the payout on</label>
                    <select data-testid="ref-basis-select" className={inputCls} value={refFor.basis} onChange={(e) => setRefFor({ ...refFor, basis: e.target.value })}>
                      <option value="contract_value">What the client pays Elevare (job/contract value)</option>
                      <option value="client_sales">The client's sales / revenue</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={labelCls}>Base amount ($)</label><input data-testid="ref-revenue-input" type="number" className={inputCls} value={refFor.revenue_to_date} onChange={(e) => setRefFor({ ...refFor, revenue_to_date: e.target.value })} placeholder="0" /></div>
                    <div><label className={labelCls}>Payout % (auto: {tier}%)</label><input data-testid="ref-pct-input" type="number" className={inputCls} value={refFor.commission_pct ?? ""} onChange={(e) => setRefFor({ ...refFor, commission_pct: e.target.value })} placeholder={`${tier}`} /></div>
                  </div>
                  <div><label className={labelCls}>Flat fee ($)</label><input data-testid="ref-flat-input" type="number" className={inputCls} value={refFor.flat_fee ?? ""} onChange={(e) => setRefFor({ ...refFor, flat_fee: e.target.value })} placeholder="0" /></div>
                  <div className="rounded-xl bg-[#1e3d3a]/5 p-3" data-testid="ref-payout-preview">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">One-time payout</div>
                    <div className="text-2xl font-semibold text-[#a3823f] tabular-nums">${pctPayout.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                    {override != null && override > cap && <div className="text-[11px] text-[#b23a48]">Capped to {cap}%</div>}
                  </div>
                </>
              )}

              {mode === "lump" && (
                <>
                  <div>
                    <label className={labelCls}>Lump-sum payout ($)</label>
                    <div className="flex gap-2 items-center">
                      <input data-testid="ref-lump-input" type="number" className={inputCls} value={refFor.lump_sum ?? ""} onChange={(e) => setRefFor({ ...refFor, lump_sum: e.target.value })} placeholder={`${lumpSuggested}`} />
                      <button onClick={() => setRefFor({ ...refFor, lump_sum: lumpSuggested })} className={btnGhost + " whitespace-nowrap"}>Use ${lumpSuggested}</button>
                    </div>
                  </div>
                  <div className="rounded-xl bg-[#1e3d3a]/5 p-3" data-testid="ref-payout-preview">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">Partner gets paid</div>
                    <div className="text-2xl font-semibold text-[#a3823f] tabular-nums">${(Number(refFor.lump_sum) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  </div>
                </>
              )}

              <div>
                <label className={labelCls}>Status</label>
                <select data-testid="ref-status-input" className={inputCls} value={refFor.status} onChange={(e) => setRefFor({ ...refFor, status: e.target.value })}>
                  {REF_STATUSES.map((s) => <option key={s} value={s}>{REF_STATUS_LABEL[s]}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button data-testid="confirm-referral-btn" onClick={addReferral} className={btnGold}><UserPlus className="h-4 w-4" /> Log deal</button>
              <button onClick={() => setRefFor(null)} className={btnGhost}>Cancel</button>
            </div>
          </div>
        </div>
      ); })()}

      {/* Payout modal */}
      {payFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPayFor(null)} data-testid="payout-modal">
          <div className="w-full max-w-md rounded-2xl bg-[#faf8f2] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-[#1e3d3a] mb-1">Pay {payFor.name}</h3>
            <div className="grid grid-cols-3 gap-2 my-4">
              {[["Earned", payFor.earned], ["Paid", payFor.paid], ["Owed", payFor.owed]].map(([l, v], i) => (
                <div key={i} className={`rounded-lg px-2 py-2 text-center ${i === 2 ? "bg-[#c9a961]/12" : "bg-[#1e3d3a]/5"}`}>
                  <div className="text-[9px] uppercase tracking-wide text-[#1e3d3a]/45">{l}</div>
                  <div className={`text-sm font-semibold tabular-nums ${i === 2 ? "text-[#a3823f]" : "text-[#1e3d3a]"}`}>{money(v)}</div>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <div><label className={labelCls}>Amount ($)</label><input data-testid="payout-amount" type="number" className={inputCls} value={payFor.amount} onChange={(e) => setPayFor({ ...payFor, amount: e.target.value })} /></div>
              <div>
                <label className={labelCls}>Method</label>
                <div className="flex gap-2">
                  <button data-testid="payout-mode-stripe" onClick={() => setPayFor({ ...payFor, mode: "stripe" })} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${payFor.mode === "stripe" ? "border-[#c9a961] bg-[#c9a961]/10 text-[#a3823f]" : "border-[#1e3d3a]/15 text-[#1e3d3a]/60"}`}>Stripe transfer</button>
                  <button data-testid="payout-mode-manual" onClick={() => setPayFor({ ...payFor, mode: "manual" })} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${payFor.mode === "manual" ? "border-[#c9a961] bg-[#c9a961]/10 text-[#a3823f]" : "border-[#1e3d3a]/15 text-[#1e3d3a]/60"}`}>Mark paid (manual)</button>
                </div>
                {payFor.mode === "stripe" && !payFor.connect_ready && (
                  <p className="mt-1.5 text-[11px] text-[#a3823f]">Partner hasn't finished Stripe onboarding — this will be recorded as pending until they connect a bank via their portal.</p>
                )}
              </div>
              {payFor.mode === "manual" && (
                <div><label className={labelCls}>How paid (optional)</label><input data-testid="payout-method" className={inputCls} value={payFor.method} onChange={(e) => setPayFor({ ...payFor, method: e.target.value })} placeholder="ACH, Zelle, Check…" /></div>
              )}
            </div>
            <div className="flex gap-2 mt-5">
              <button data-testid="confirm-payout-btn" onClick={submitPayout} className={btnGold}><Send className="h-4 w-4" /> Record payout</button>
              <button onClick={() => setPayFor(null)} className={btnGhost}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {delTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDelTarget(null)} data-testid="delete-modal">
          <div className="w-full max-w-sm rounded-2xl bg-[#faf8f2] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-[#1e3d3a]">Remove partner?</h3>
            <p className="text-sm text-[#1e3d3a]/60 mt-2">This permanently deletes <b>{delTarget.partner_name}</b> and their referral history.</p>
            <div className="flex gap-2 mt-5">
              <button data-testid="confirm-delete-btn" onClick={doDelete} className="inline-flex items-center gap-1.5 rounded-full bg-[#b23a48] px-4 py-2 text-xs font-semibold text-white hover:bg-[#8f2e3a]"><Trash2 className="h-4 w-4" /> Delete</button>
              <button onClick={() => setDelTarget(null)} className={btnGhost}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
